/* ============================================================================
   Sticky Add to Cart
   ============================================================================
   Shows a sticky add to cart bar when user scrolls past the main product form
   ========================================================================== */

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.productForm = document.querySelector('form[data-product-form]') || document.querySelector('.product-form');
    this.quantityInput = this.querySelector('.sticky-atc__quantity-input');
    this.quantityButtons = this.querySelectorAll('.sticky-atc__quantity-button');
    this.variantSelect = this.querySelector('.sticky-atc__variant-select select');
    this.addButton = this.querySelector('.sticky-atc__add-button');

    this.isVisible = false;
    this.offset = 100; // Offset from top to trigger sticky bar

    this.init();
  }

  init() {
    this.setupScrollListener();
    this.setupQuantityButtons();
    this.setupVariantSelector();
    this.setupAddToCart();
    this.syncWithMainForm();
  }

  setupScrollListener() {
    let ticking = false;

    const checkPosition = () => {
      if (!this.productForm) return;

      const formRect = this.productForm.getBoundingClientRect();
      const formBottom = formRect.bottom;
      const shouldShow = formBottom < this.offset;

      if (shouldShow !== this.isVisible) {
        this.isVisible = shouldShow;
        this.classList.toggle('is-visible', shouldShow);
      }

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(checkPosition);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    checkPosition(); // Initial check
  }

  setupQuantityButtons() {
    if (!this.quantityInput) return;

    this.quantityButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const action = button.dataset.action;
        const currentValue = parseInt(this.quantityInput.value) || 1;

        if (action === 'increase') {
          this.quantityInput.value = currentValue + 1;
        } else if (action === 'decrease' && currentValue > 1) {
          this.quantityInput.value = currentValue - 1;
        }

        // Sync with main form
        this.syncQuantityToMainForm(this.quantityInput.value);
      });
    });

    this.quantityInput.addEventListener('change', () => {
      const value = parseInt(this.quantityInput.value) || 1;
      this.quantityInput.value = Math.max(1, value);
      this.syncQuantityToMainForm(this.quantityInput.value);
    });
  }

  setupVariantSelector() {
    if (!this.variantSelect) return;

    this.variantSelect.addEventListener('change', () => {
      const selectedVariantId = this.variantSelect.value;
      this.syncVariantToMainForm(selectedVariantId);
      this.updatePrice();
      this.updateAvailability();
    });
  }

  setupAddToCart() {
    if (!this.addButton) return;

    this.addButton.addEventListener('click', async (e) => {
      e.preventDefault();

      if (this.addButton.disabled) return;

      // Get form data
      const formData = new FormData();
      const variantId = this.variantSelect ? this.variantSelect.value : this.dataset.variantId;
      const quantity = this.quantityInput ? this.quantityInput.value : 1;

      if (!variantId) {
        console.error('No variant selected');
        return;
      }

      formData.append('id', variantId);
      formData.append('quantity', quantity);

      // Set loading state
      this.addButton.classList.add('loading');
      this.addButton.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const data = await response.json();

        // Trigger cart update events
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Success feedback
        const originalText = this.addButton.textContent;
        this.addButton.textContent = '✓ Added!';

        setTimeout(() => {
          this.addButton.textContent = originalText;
        }, 2000);

        // Open cart drawer if available
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.open();
        }

      } catch (error) {
        console.error('Error adding to cart:', error);
        this.addButton.textContent = 'Error - Try again';

        setTimeout(() => {
          this.addButton.textContent = 'Add to Cart';
        }, 2000);
      } finally {
        this.addButton.classList.remove('loading');
        this.addButton.disabled = false;
      }
    });
  }

  syncWithMainForm() {
    if (!this.productForm) return;

    // Sync variant changes from main form
    const mainVariantSelect = this.productForm.querySelector('select[name="id"]');
    if (mainVariantSelect && this.variantSelect) {
      mainVariantSelect.addEventListener('change', () => {
        this.variantSelect.value = mainVariantSelect.value;
        this.updatePrice();
        this.updateAvailability();
      });
    }

    // Sync quantity changes from main form
    const mainQuantityInput = this.productForm.querySelector('input[name="quantity"]');
    if (mainQuantityInput && this.quantityInput) {
      mainQuantityInput.addEventListener('change', () => {
        this.quantityInput.value = mainQuantityInput.value;
      });
    }
  }

  syncQuantityToMainForm(quantity) {
    if (!this.productForm) return;

    const mainQuantityInput = this.productForm.querySelector('input[name="quantity"]');
    if (mainQuantityInput) {
      mainQuantityInput.value = quantity;
    }
  }

  syncVariantToMainForm(variantId) {
    if (!this.productForm) return;

    const mainVariantSelect = this.productForm.querySelector('select[name="id"]');
    if (mainVariantSelect) {
      mainVariantSelect.value = variantId;
      mainVariantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updatePrice() {
    if (!this.variantSelect) return;

    const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
    const price = selectedOption.dataset.price;
    const comparePrice = selectedOption.dataset.comparePrice;

    const priceElement = this.querySelector('.sticky-atc__price');
    if (priceElement && price) {
      priceElement.innerHTML = price;

      if (comparePrice && comparePrice !== price) {
        priceElement.innerHTML += `<span class="sticky-atc__compare-price">${comparePrice}</span>`;
      }
    }
  }

  updateAvailability() {
    if (!this.variantSelect || !this.addButton) return;

    const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
    const available = selectedOption.dataset.available === 'true';

    this.addButton.disabled = !available;
    this.addButton.textContent = available ? 'Add to Cart' : 'Sold Out';
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
